/**
 * Phase 8 — Project intake gate.
 *
 * Shown before the chat workspace renders for any new project. Customer
 * supplies (or reuses) a delivery address and a phone number; submit
 * creates the estimate and the parent navigates to the workspace.
 *
 * Phone is remembered on `UserProfile.phone` after the first project,
 * so returning customers see it prefilled. Address is per-estimate;
 * returning customers see up to five "Same as <prior>" reuse cards.
 */

import { useMemo, useState } from "react";

import { createEstimate } from "@starz/shared/api/sdk.gen";
import type {
  Address,
  Estimate,
  EstimateCreateRequest,
} from "@starz/shared/api/types.gen";
import { reuseCandidates, type ReuseCandidate } from "@starz/shared/workspace/reuseCandidates";

interface Props {
  /** All of the customer's prior estimates, used to populate reuse cards. */
  priorEstimates: Estimate[];
  /** Customer's stored phone, if any (from `UserProfile.phone`). */
  initialPhone?: string | null;
  /** Called after the new estimate is created. */
  onCreated: (estimate: Estimate) => void;
  /** Cancel the modal — parent decides what to do (typically goBack). */
  onCancel?: () => void;
}

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "DC", "PR",
];

const REUSE_VISIBLE_DEFAULT = 5;

interface AddressFormState {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

const EMPTY_ADDRESS: AddressFormState = {
  line1: "",
  line2: "",
  city: "",
  state: "TX",
  zip: "",
};

export function NewProjectModal(props: Props) {
  const candidates = useMemo(
    () => reuseCandidates(props.priorEstimates),
    [props.priorEstimates],
  );
  const [showAllCandidates, setShowAllCandidates] = useState(false);
  const visibleCandidates = showAllCandidates
    ? candidates
    : candidates.slice(0, REUSE_VISIBLE_DEFAULT);

  // Mode: "reuse" (selected an existing address) | "new" (typing a fresh one).
  const [reuseId, setReuseId] = useState<string | null>(
    candidates.length > 0 ? candidates[0].estimate_id : null,
  );
  const [addressForm, setAddressForm] = useState<AddressFormState>(EMPTY_ADDRESS);
  const [phone, setPhone] = useState<string>(props.initialPhone ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inReuseMode = reuseId !== null;

  function selectReuse(id: string) {
    setReuseId(id);
    setError(null);
  }

  function selectNewAddress() {
    setReuseId(null);
    setError(null);
  }

  function patchAddress(patch: Partial<AddressFormState>) {
    setAddressForm((prev) => ({ ...prev, ...patch }));
  }

  function isAddressFormComplete(a: AddressFormState): boolean {
    return (
      a.line1.trim().length > 0 &&
      a.city.trim().length > 0 &&
      a.state.length === 2 &&
      /^\d{5}$/.test(a.zip)
    );
  }

  const canSubmit =
    !submitting &&
    phone.trim().length >= 7 &&
    (inReuseMode || isAddressFormComplete(addressForm));

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    const idempotencyKey =
      typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const body: EstimateCreateRequest = inReuseMode
      ? { phone, reuse_from_estimate_id: reuseId! }
      : {
          phone,
          address: {
            line1: addressForm.line1.trim(),
            line2: addressForm.line2.trim() || null,
            city: addressForm.city.trim(),
            state: addressForm.state,
            zip: addressForm.zip,
          } as Address,
        };

    const result = await createEstimate({ body, idempotencyKey });
    setSubmitting(false);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    props.onCreated(result.data);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Start a new project"
      className="fixed inset-0 z-50 flex items-center justify-center bg-starz-charcoal/40 px-4 py-8"
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-xl space-y-5 rounded-2xl bg-white p-6 shadow-xl"
      >
        <header>
          <h2 className="font-serif text-2xl text-starz-navy">Start a new project</h2>
          <p className="mt-1 text-sm text-warm-gray">
            We need a few details before Stella can begin. Your contractor uses these to
            schedule a site visit.
          </p>
        </header>

        {candidates.length > 0 && (
          <section aria-label="Reuse a previous address" className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-starz-charcoal">
              Project address
            </p>
            <div className="grid grid-cols-1 gap-2">
              {visibleCandidates.map((c) => (
                <ReuseCard
                  key={c.estimate_id}
                  candidate={c}
                  selected={reuseId === c.estimate_id}
                  onSelect={() => selectReuse(c.estimate_id)}
                />
              ))}
              {candidates.length > REUSE_VISIBLE_DEFAULT && !showAllCandidates && (
                <button
                  type="button"
                  onClick={() => setShowAllCandidates(true)}
                  className="text-left text-sm font-semibold text-starz-gold hover:underline"
                >
                  More addresses ({candidates.length - REUSE_VISIBLE_DEFAULT})…
                </button>
              )}
              <NewAddressCard selected={!inReuseMode} onSelect={selectNewAddress} />
            </div>
          </section>
        )}

        {(!inReuseMode || candidates.length === 0) && (
          <fieldset
            disabled={inReuseMode}
            className="space-y-3 rounded-xl border border-starz-gold/20 p-4"
          >
            <legend className="px-1 text-xs font-semibold uppercase tracking-widest text-starz-charcoal">
              {candidates.length > 0 ? "New address" : "Project address"}
            </legend>
            <Field label="Street address" htmlFor="addr-line1">
              <input
                id="addr-line1"
                type="text"
                autoComplete="address-line1"
                required={!inReuseMode}
                maxLength={200}
                value={addressForm.line1}
                onChange={(e) => patchAddress({ line1: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Apt, suite, etc. (optional)" htmlFor="addr-line2">
              <input
                id="addr-line2"
                type="text"
                autoComplete="address-line2"
                maxLength={200}
                value={addressForm.line2}
                onChange={(e) => patchAddress({ line2: e.target.value })}
                className={inputCls}
              />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="City" htmlFor="addr-city" className="col-span-1">
                <input
                  id="addr-city"
                  type="text"
                  autoComplete="address-level2"
                  required={!inReuseMode}
                  maxLength={80}
                  value={addressForm.city}
                  onChange={(e) => patchAddress({ city: e.target.value })}
                  className={inputCls}
                />
              </Field>
              <Field label="State" htmlFor="addr-state">
                <select
                  id="addr-state"
                  autoComplete="address-level1"
                  required={!inReuseMode}
                  value={addressForm.state}
                  onChange={(e) => patchAddress({ state: e.target.value })}
                  className={inputCls}
                >
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="ZIP" htmlFor="addr-zip">
                <input
                  id="addr-zip"
                  type="text"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  required={!inReuseMode}
                  pattern="\d{5}"
                  maxLength={5}
                  value={addressForm.zip}
                  onChange={(e) =>
                    patchAddress({ zip: e.target.value.replace(/\D/g, "").slice(0, 5) })
                  }
                  className={inputCls}
                />
              </Field>
            </div>
          </fieldset>
        )}

        <Field label="Phone number" htmlFor="intake-phone">
          <input
            id="intake-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            required
            placeholder="(713) 555-0144"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className={inputCls}
            aria-describedby="phone-help"
          />
        </Field>
        <p id="phone-help" className="text-xs text-warm-gray">
          We'll only use this for project-related contact from your contractor.
        </p>

        {error && (
          <div role="alert" className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
          {props.onCancel && (
            <button
              type="button"
              onClick={props.onCancel}
              className="rounded-lg border border-starz-charcoal/20 px-5 py-2 text-sm font-semibold text-starz-charcoal hover:bg-starz-cream/40"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={!canSubmit}
            className="min-h-[44px] rounded-lg bg-starz-navy px-5 py-2 text-sm font-semibold uppercase tracking-wide text-starz-off-white hover:bg-starz-navy/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? "Starting…" : "Start Project"}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  "block w-full rounded-lg border border-starz-charcoal/20 bg-white px-3 py-2 text-sm text-starz-charcoal focus:border-starz-gold focus:outline-none focus:ring-1 focus:ring-starz-gold disabled:cursor-not-allowed disabled:bg-starz-cream/30";

function Field(props: {
  label: string;
  htmlFor: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label htmlFor={props.htmlFor} className={`block text-xs ${props.className ?? ""}`}>
      <span className="mb-1 block font-semibold uppercase tracking-widest text-starz-charcoal">
        {props.label}
      </span>
      {props.children}
    </label>
  );
}

function ReuseCard(props: {
  candidate: ReuseCandidate;
  selected: boolean;
  onSelect: () => void;
}) {
  const a = props.candidate.address;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={props.selected}
      onClick={props.onSelect}
      className={`rounded-lg border p-3 text-left text-sm transition ${
        props.selected
          ? "border-starz-gold bg-starz-cream/40"
          : "border-starz-charcoal/15 bg-white hover:border-starz-gold/60"
      }`}
    >
      <div className="font-medium text-starz-navy">
        Same as {a.line1}
        {a.line2 ? `, ${a.line2}` : ""}
      </div>
      <div className="mt-0.5 text-xs text-warm-gray">
        {a.city}, {a.state} {a.zip}
        {props.candidate.label && ` — ${props.candidate.label}`}
      </div>
    </button>
  );
}

function NewAddressCard(props: { selected: boolean; onSelect: () => void }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={props.selected}
      onClick={props.onSelect}
      className={`rounded-lg border p-3 text-left text-sm transition ${
        props.selected
          ? "border-starz-gold bg-starz-cream/40"
          : "border-dashed border-starz-charcoal/30 bg-white hover:border-starz-gold/60"
      }`}
    >
      <div className="font-medium text-starz-navy">Use a new address</div>
    </button>
  );
}
